// ViewPage.tsx — SUBSTITUIR TODO O ARQUIVO
// ✅ NOVO: supportContent exibido dentro do card expandido, com toggle

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, BookOpen, Plus, Trash2, ChevronDown, ChevronUp, Search, X as XIcon } from 'lucide-react';
import './ViewPage.css';
import Toast from '../components/Toast';
import CustomModal from '../components/CustomModal';
import { useAuth } from '../contexts/AuthContext';
import { useAntiPiracy } from '../hooks/useAntiPiracy';
import StudyModeModal from '../components/study/StudyModeModal';
import { ViewPageMediaRenderer } from '../components/view/ViewPageMediaRenderer';
import { ImageZoomModal } from '../components/CardEditor/ImageZoomModal';

interface Flashcard {
    _id?: string;
    front: string;
    back: string;
    supportContent?: string; // ✅ NOVO
    media?: {
        images?: Array<{ url: string; side: string }>;
        audios?: Array<{ url: string; side: string }>;
    };
}

interface ViewPageProps {
    isCustomDeck?: boolean;
}

type ViewContext = 'platform' | 'custom-manage' | 'custom-addcards';

const ViewPage: React.FC<ViewPageProps> = ({ isCustomDeck = false }) => {
    const { deckId, subdeckId } = useParams<{ deckId: string; subdeckId: string }>();
    const navigate = useNavigate();

    const [deckName, setDeckName] = useState('');
    const [subdeckName, setSubdeckName] = useState('');
    const [cards, setCards] = useState<Flashcard[]>([]);
    const [loading, setLoading] = useState(true);
    const [expandedCard, setExpandedCard] = useState<string | null>(null);
    // ✅ NOVO: controla qual card está com o conteúdo de apoio aberto
    const [expandedSupport, setExpandedSupport] = useState<string | null>(null);

    const searchParams = new URLSearchParams(window.location.search);
    const addToCustomDeck = searchParams.get('addTo');
    const addToSubdeck = searchParams.get('subdeck');

    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [cardToRemove, setCardToRemove] = useState<{ id: string; index: number } | null>(null);
    const { isAdmin, isEditor, loading: authLoading } = useAuth();
    useAntiPiracy(!authLoading && !isAdmin && !isEditor);
    const [showModeModal, setShowModeModal] = useState(false);
    const [addedCardIds, setAddedCardIds] = useState<Set<string>>(new Set());

    const [zoomedImage, setZoomedImage] = useState<string | null>(null);
    const [zoomScale, setZoomScale] = useState(1);

    // ── Buscar no conteúdo (find-in-page, tipo Ctrl+F do navegador) ──
    // Existe porque nem todo mundo usa um navegador com essa função (ex:
    // apps embutidos, alguns navegadores mobile) — busca sobre o TEXTO CRU
    // dos cards (não o DOM renderizado), o que também deixa achar ocorrências
    // dentro de cards ainda fechados (verso/conteúdo de apoio), expandindo o
    // card certo e rolando até a ocorrência ao navegar.
    const [findOpen, setFindOpen] = useState(false);
    const [findQuery, setFindQuery] = useState('');
    const [findMatchIndex, setFindMatchIndex] = useState(0);

    const location = useLocation();

    const isCustomPath = window.location.pathname.includes('/custom-decks/');
    const effectiveIsCustomDeck = isCustomDeck || isCustomPath;
    const isFromAddCards = !!addToCustomDeck;

    const getViewContext = (): ViewContext => {
        if (!effectiveIsCustomDeck && !isFromAddCards) return 'platform';
        if (effectiveIsCustomDeck && !isFromAddCards) return 'custom-manage';
        if (isFromAddCards) return 'custom-addcards';
        return 'platform';
    };

    const viewContext = getViewContext();
    const showAddButton = viewContext === 'custom-addcards';
    const showRemoveButton = viewContext === 'custom-manage' || viewContext === 'custom-addcards';

    // Aplica e persiste o contexto de tema no mount
    useEffect(() => {
        const isRyan = (location.state as any)?.fromRyan === true
            || sessionStorage.getItem('graia_theme_context') === 'ryan';
        sessionStorage.setItem('graia_theme_context', isRyan ? 'ryan' : 'enem');
        if (isRyan) {
            document.body.classList.add('theme-ryan');
        } else {
            document.body.classList.remove('theme-ryan');
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadSubdeck = useCallback(async () => {
        const prefetchedData = location.state?.prefetchedData;
        if (prefetchedData) {
            setDeckName(prefetchedData.deckName);
            setSubdeckName(prefetchedData.subdeck.name || '');
            setCards(prefetchedData.subdeck.cards || []);
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');
            if (!token) { navigate('/'); return; }

            const baseUrl = effectiveIsCustomDeck
                ? `/api/custom-decks/${deckId}/subdecks/${subdeckId}`
                : `/api/decks/${deckId}/subdecks/${subdeckId}`;

            const response = await fetch(baseUrl, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                setDeckName(data.deckName);
                setSubdeckName(data.subdeck.name || data.subdeck.title || '');
                setCards(data.subdeck.cards || []);
            }
        } catch (error) {
            console.error('Erro:', error);
        } finally {
            setLoading(false);
        }
    }, [deckId, subdeckId, effectiveIsCustomDeck, navigate, location.state]);

    const loadAddedCards = useCallback(async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(
                `/api/custom-decks/${addToCustomDeck}/subdecks/${addToSubdeck}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.ok) {
                const data = await response.json();
                const addedIds = new Set<string>(data.subdeck.cards.map((c: any) => c.cardId));
                setAddedCardIds(addedIds);
            }
        } catch (error) {
            console.error('Erro ao carregar cards adicionados:', error);
        }
    }, [addToCustomDeck, addToSubdeck]);

    useEffect(() => { loadSubdeck(); }, [loadSubdeck]);

    useEffect(() => {
        if (viewContext === 'custom-addcards' && addToCustomDeck && addToSubdeck) {
            loadAddedCards();
        }
    }, [viewContext, addToCustomDeck, addToSubdeck, loadAddedCards]);

    // ✅ Quando o card é colapsado, fecha também o conteúdo de apoio
    const handleToggleCard = (cardKey: string) => {
        const isOpening = expandedCard !== cardKey;
        setExpandedCard(isOpening ? cardKey : null);
        if (!isOpening) {
            // fechando o card → fecha o apoio junto
            setExpandedSupport(null);
        }
    };

    // ✅ Toggle do conteúdo de apoio (só funciona se a resposta está aberta)
    const handleToggleSupport = (e: React.MouseEvent, cardKey: string) => {
        e.stopPropagation();
        setExpandedSupport(prev => prev === cardKey ? null : cardKey);
    };

    const confirmRemoveCard = async () => {
        if (!cardToRemove) return;

        try {
            const token = localStorage.getItem('accessToken');

            let targetDeckId: string;
            let targetSubdeckIdValue: string;

            if (viewContext === 'custom-addcards') {
                if (!addToCustomDeck || !addToSubdeck) {
                    setToast({ message: 'Erro: Destino não identificado', type: 'error' });
                    return;
                }
                targetDeckId = addToCustomDeck;
                targetSubdeckIdValue = addToSubdeck;
            } else {
                if (!deckId || !subdeckId) {
                    setToast({ message: 'Erro: Deck não identificado', type: 'error' });
                    return;
                }
                targetDeckId = deckId;
                targetSubdeckIdValue = subdeckId;
            }

            const cardIdToRemove = cards[cardToRemove.index]?._id || cardToRemove.id;

            const response = await fetch(
                `/api/custom-decks/${targetDeckId}/subdecks/${targetSubdeckIdValue}/cards/${cardIdToRemove}`,
                {
                    method: 'DELETE',
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.ok) {
                const toastMessage = viewContext === 'custom-addcards'
                    ? 'Card removido do seu deck personalizado'
                    : 'Card removido com sucesso';

                setToast({ message: toastMessage, type: 'success' });
                setShowRemoveModal(false);
                setCardToRemove(null);

                if (viewContext === 'custom-addcards') {
                    if (cardIdToRemove) {
                        setAddedCardIds(prev => {
                            const newSet = new Set(prev);
                            newSet.delete(cardIdToRemove);
                            return newSet;
                        });
                    }
                } else {
                    setCards(prevCards => prevCards.filter((_, i) => i !== cardToRemove.index));
                }
            } else {
                const error = await response.json();
                setToast({ message: error.message, type: 'error' });
            }
        } catch (error) {
            console.error('Erro ao remover card:', error);
            setToast({ message: 'Erro ao remover card', type: 'error' });
        }
    };

    const handleAddCard = async (card: Flashcard) => {
        try {
            const token = localStorage.getItem('accessToken');

            const response = await fetch(
                `/api/custom-decks/${addToCustomDeck}/subdecks/${addToSubdeck}/cards`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        deckId,
                        subdeckId,
                        cardId: card._id,
                        front: card.front,
                        back: card.back
                    })
                }
            );

            if (response.ok) {
                setToast({ message: 'Card adicionado com sucesso', type: 'success' });
                if (card._id) {
                    setAddedCardIds(prev => {
                        const newSet = new Set(prev);
                        newSet.add(card._id!);
                        return newSet;
                    });
                }
            } else {
                const error = await response.json();
                setToast({ message: error.message || 'Erro ao adicionar card', type: 'error' });
            }
        } catch (error) {
            console.error('Erro:', error);
            setToast({ message: 'Erro ao adicionar card', type: 'error' });
        }
    };

    const renderContent = (text: string, findHighlight?: { query: string; currentLocalIndex: number | null }) => {
        let html = text;

        // Marca as ocorrências da busca ANTES das outras substituições de
        // markdown — o regex de negrito/etc usa `.` (que casa com as tags
        // <mark> inseridas aqui), então continuam funcionando normalmente por
        // cima do destaque.
        if (findHighlight && findHighlight.query) {
            const escaped = findHighlight.query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const re = new RegExp(escaped, 'gi');
            let occurrence = -1;
            html = html.replace(re, (match) => {
                occurrence++;
                const isCurrent = occurrence === findHighlight.currentLocalIndex;
                return `<mark class="vizw-find-hl${isCurrent ? ' vizw-find-hl--current' : ''}"${isCurrent ? ' data-find-current="true"' : ''}>${match}</mark>`;
            });
        }

        html = html
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            .replace(/\*(.*?)\*/g, '<em>$1</em>')
            .replace(/_{4,}/g, match => `<span class="vizw-blank">${'_'.repeat(match.length)}</span>`)
            .replace(/__(.*?)__/g, '<u>$1</u>')
            .replace(/==(.*?)==/g, '<mark>$1</mark>')
            .replace(/!\[.*?\]\((.*?)\)/g, '<img src="$1" alt="Imagem" style="cursor: pointer;" />')
            .replace(/\n/g, '<br>');

        return <div dangerouslySetInnerHTML={{ __html: html }} />;
    };

    // Achata front/back/supportContent de TODOS os cards numa lista única de
    // ocorrências, na ordem em que aparecem na página — permite navegar
    // "próxima/anterior" cruzando cards, igual ao Ctrl+F do navegador.
    interface FindMatch { cardKey: string; side: 'front' | 'back' | 'support'; localIndex: number; }
    const findMatches = useMemo<FindMatch[]>(() => {
        const q = findQuery.trim();
        if (!q) return [];
        const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const re = new RegExp(escaped, 'gi');
        const result: FindMatch[] = [];
        cards.forEach((card, index) => {
            const cardKey = card._id || `${index}`;
            (['front', 'back', 'support'] as const).forEach(side => {
                const text = side === 'front' ? card.front : side === 'back' ? card.back : (card.supportContent || '');
                if (!text) return;
                re.lastIndex = 0;
                let localIndex = 0;
                while (re.exec(text) !== null) {
                    result.push({ cardKey, side, localIndex });
                    localIndex++;
                }
            });
        });
        return result;
    }, [cards, findQuery]);

    const safeFindIndex = findMatches.length > 0 ? Math.min(findMatchIndex, findMatches.length - 1) : 0;
    const activeFindMatch = findMatches.length > 0 ? findMatches[safeFindIndex] : null;

    const getFindHighlight = (cardKey: string, side: 'front' | 'back' | 'support') => {
        if (!findQuery.trim()) return undefined;
        const isActiveSegment = !!activeFindMatch && activeFindMatch.cardKey === cardKey && activeFindMatch.side === side;
        return { query: findQuery, currentLocalIndex: isActiveSegment ? activeFindMatch!.localIndex : null };
    };

    // Zera a posição sempre que o termo muda (senão manteria um índice de
    // uma busca anterior maior que a nova lista de ocorrências).
    useEffect(() => {
        setFindMatchIndex(0);
    }, [findQuery]);

    // Expande o card certo (verso/apoio ficam fechados por padrão) e rola até
    // a ocorrência atual sempre que ela muda.
    useEffect(() => {
        if (!findOpen || !activeFindMatch) return;
        if (activeFindMatch.side === 'back' || activeFindMatch.side === 'support') {
            setExpandedCard(activeFindMatch.cardKey);
        }
        if (activeFindMatch.side === 'support') {
            setExpandedSupport(activeFindMatch.cardKey);
        }
        const timer = setTimeout(() => {
            document.querySelector('[data-find-current="true"]')
                ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 60);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [safeFindIndex, activeFindMatch?.cardKey, activeFindMatch?.side, findOpen]);

    const goToNextFindMatch = () => {
        if (findMatches.length === 0) return;
        setFindMatchIndex(i => (i + 1) % findMatches.length);
    };
    const goToPrevFindMatch = () => {
        if (findMatches.length === 0) return;
        setFindMatchIndex(i => (i - 1 + findMatches.length) % findMatches.length);
    };
    const closeFind = () => {
        setFindOpen(false);
        setFindQuery('');
    };

    // A GRAIA Medicina (Ryan) não tem o conceito de "modo de estudo" (oficial/
    // aleatório/etc) que o modal abaixo oferece — isso é só da plataforma
    // ENEM. Lá o botão "Estudar" sempre vai direto pro StudyPage do mesmo
    // deck/subdeck (mesmo destino que RyanSubdeckCard.tsx/goStudy usa),
    // reaproveitando o state que trouxe a pessoa até este ViewPage (já tem
    // fromRyan/returnPath/spaceId/spaceName — o StudyPage precisa disso pra
    // saber voltar pro campo de estudo certo).
    const handleStudyClick = () => {
        const isRyanCtx = (location.state as any)?.fromRyan === true
            || sessionStorage.getItem('graia_theme_context') === 'ryan';
        if (isRyanCtx) {
            navigate(`/study/${deckId}/${subdeckId}`, { state: location.state });
            return;
        }
        setShowModeModal(true);
    };

    const handleGoBack = () => {
        const savedContext = sessionStorage.getItem('subdecksContext');
        const context = savedContext ? JSON.parse(savedContext) : {};

        if (viewContext === 'custom-addcards' && addToCustomDeck && addToSubdeck) {
            navigate(`/add-cards/${addToCustomDeck}/${addToSubdeck}`, { state: context });
            return;
        }

        // Detectar contexto Ryan
        const fromRyan = (location.state as any)?.fromRyan === true;
        const ryanReturnPath = (location.state as any)?.returnPath;
        if (fromRyan && ryanReturnPath) {
            navigate(ryanReturnPath);
            return;
        }

        sessionStorage.removeItem('graia_theme_context');
        const subdecksPath = effectiveIsCustomDeck
            ? `/custom-decks/${deckId}/subdecks`
            : `/subdecks/${deckId}`;

        navigate(subdecksPath, { state: context });
    };

    if (loading) {
        return (
            <div className="vizw-page">
                <div className="vizw-loading"></div>
            </div>
        );
    }

    return (
        <div className="vizw-page">
            <header className="vizw-header-fixed">
                <div className="vizw-header-content">
                    <button className="vizw-btn-back" onClick={handleGoBack}>
                        <ArrowLeft size={18} />
                        Voltar
                    </button>

                    <div className="vizw-header-info">
                        <h1 className="vizw-deck-name">{deckName}</h1>
                        {subdeckName && <h2 className="vizw-subdeck-name">{subdeckName}</h2>}
                    </div>

                    <div className="vizw-header-meta">
                        <span className="vizw-card-count">{cards.length} cards</span>
                    </div>

                    {cards.length > 0 && (
                        <button
                            className={`vizw-btn-find-toggle ${findOpen ? 'vizw-btn-find-toggle--active' : ''}`}
                            onClick={() => setFindOpen(o => !o)}
                            title="Pesquisar no conteúdo"
                        >
                            <Search size={18} />
                        </button>
                    )}

                    {cards.length > 0 && (
                        <button
                            className="vizw-btn-study"
                            onClick={handleStudyClick}
                        >
                            <BookOpen size={18} />
                            Estudar
                        </button>
                    )}
                </div>
            </header>

            {findOpen && (
                <div className="vizw-find-panel">
                    <Search size={15} className="vizw-find-panel-icon" />
                    <input
                        autoFocus
                        className="vizw-find-input"
                        placeholder="Pesquisar no conteúdo..."
                        value={findQuery}
                        onChange={e => setFindQuery(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                e.shiftKey ? goToPrevFindMatch() : goToNextFindMatch();
                            } else if (e.key === 'Escape') {
                                closeFind();
                            }
                        }}
                    />
                    <span className="vizw-find-count">
                        {findQuery.trim() === ''
                            ? ''
                            : findMatches.length > 0
                                ? `${safeFindIndex + 1}/${findMatches.length}`
                                : '0/0'}
                    </span>
                    <button
                        className="vizw-find-nav-btn"
                        onClick={goToPrevFindMatch}
                        disabled={findMatches.length === 0}
                        title="Anterior (Shift+Enter)"
                    >
                        <ChevronUp size={15} />
                    </button>
                    <button
                        className="vizw-find-nav-btn"
                        onClick={goToNextFindMatch}
                        disabled={findMatches.length === 0}
                        title="Próxima (Enter)"
                    >
                        <ChevronDown size={15} />
                    </button>
                    <button className="vizw-find-close-btn" onClick={closeFind} title="Fechar (Esc)">
                        <XIcon size={15} />
                    </button>
                </div>
            )}

            <main className="vizw-main">
                {cards.length === 0 ? (
                    <div className="vizw-empty-state">
                        <div className="vizw-empty-icon">📂</div>
                        <h2>Nenhum card neste subdeck</h2>
                        <p>Este subdeck ainda não possui flashcards.</p>
                    </div>
                ) : (
                    <div className="vizw-cards-list">
                        {cards.map((card, index) => {
                            const cardKey = card._id || `${index}`;
                            const isCardExpanded = expandedCard === cardKey;
                            const isSupportExpanded = expandedSupport === cardKey;
                            const hasSupportContent = !!card.supportContent?.trim();

                            return (
                                <article
                                    key={cardKey}
                                    className={`vizw-card ${isCardExpanded ? 'vizw-card--expanded' : ''}`}
                                    onClick={() => handleToggleCard(cardKey)}
                                >
                                    {viewContext === 'custom-addcards' && addedCardIds.has(card._id!) && (
                                        <div className="vizw-badge-added">✓ Adicionado</div>
                                    )}

                                    {(showAddButton || showRemoveButton) && (
                                        <div className="vizw-card-actions">
                                            {showRemoveButton && (
                                                viewContext === 'custom-manage' ||
                                                (viewContext === 'custom-addcards' && addedCardIds.has(card._id!))
                                            ) && (
                                                    <button
                                                        className="vizw-btn-action vizw-btn-remove"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (!card._id) {
                                                                setToast({ message: 'Erro: Card sem identificador', type: 'error' });
                                                                return;
                                                            }
                                                            setCardToRemove({ id: card._id, index });
                                                            setShowRemoveModal(true);
                                                        }}
                                                        title="Remover card"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                )}

                                            {showAddButton && (
                                                <button
                                                    className="vizw-btn-action vizw-btn-add"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleAddCard(card);
                                                    }}
                                                    title="Adicionar ao deck"
                                                >
                                                    <Plus size={18} />
                                                </button>
                                            )}
                                        </div>
                                    )}

                                    <div className="vizw-card-header">
                                        <span className="vizw-card-number">Card {index + 1}</span>
                                    </div>

                                    <div className="vizw-card-content">
                                        {/* FRENTE (sempre visível) */}
                                        <div className="vizw-card-side vizw-card-front">
                                            <div className="vizw-side-label">Pergunta</div>
                                            <div className="vizw-side-text">
                                                {renderContent(card.front, getFindHighlight(cardKey, 'front'))}
                                            </div>
                                            <div onClick={(e) => e.stopPropagation()}>
                                                <ViewPageMediaRenderer
                                                    imageUrl={card.media?.images?.find(img => img.side === 'front')?.url}
                                                    audioUrl={card.media?.audios?.find(aud => aud.side === 'front')?.url}
                                                    onImageClick={(url) => {
                                                        setZoomedImage(url);
                                                        setZoomScale(1);
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        {/* VERSO (só quando expandido) */}
                                        {isCardExpanded && (
                                            <>
                                                <div className="vizw-card-divider"></div>
                                                <div className="vizw-card-side vizw-card-back">
                                                    <div className="vizw-side-label">Resposta</div>
                                                    <div className="vizw-side-text">
                                                        {renderContent(card.back, getFindHighlight(cardKey, 'back'))}
                                                    </div>
                                                    <div onClick={(e) => e.stopPropagation()}>
                                                        <ViewPageMediaRenderer
                                                            imageUrl={card.media?.images?.find(img => img.side === 'back')?.url}
                                                            audioUrl={card.media?.audios?.find(aud => aud.side === 'back')?.url}
                                                            onImageClick={(url) => {
                                                                setZoomedImage(url);
                                                                setZoomScale(1);
                                                            }}
                                                        />
                                                    </div>
                                                </div>

                                                {/* ✅ NOVO: Botão + Conteúdo de Apoio */}
                                                {hasSupportContent && (
                                                    <>
                                                        <button
                                                            className={`vizw-support-toggle ${isSupportExpanded ? 'vizw-support-toggle--open' : ''}`}
                                                            onClick={(e) => handleToggleSupport(e, cardKey)}
                                                        >
                                                            {isSupportExpanded
                                                                ? <><ChevronUp size={15} /> Ocultar conteúdo de apoio</>
                                                                : <><ChevronDown size={15} /> Ver conteúdo de apoio</>
                                                            }
                                                        </button>

                                                        {isSupportExpanded && (
                                                            <div className="vizw-support-content" onClick={(e) => e.stopPropagation()}>
                                                                <div className="vizw-support-label">Conteúdo de Apoio</div>
                                                                <div className="vizw-side-text">
                                                                    {renderContent(card.supportContent!, getFindHighlight(cardKey, 'support'))}
                                                                </div>
                                                                <ViewPageMediaRenderer
                                                                    imageUrl={card.media?.images?.find(img => img.side === 'support')?.url}
                                                                    audioUrl={card.media?.audios?.find(aud => aud.side === 'support')?.url}
                                                                    onImageClick={(url) => {
                                                                        setZoomedImage(url);
                                                                        setZoomScale(1);
                                                                    }}
                                                                />
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </div>

                                    {!isCardExpanded && (
                                        <div className="vizw-click-hint">Clique para ver a resposta</div>
                                    )}
                                </article>
                            );
                        })}
                    </div>
                )}
            </main>

            {zoomedImage && (
                <ImageZoomModal
                    imageUrl={zoomedImage}
                    onClose={() => {
                        setZoomedImage(null);
                        setZoomScale(1);
                    }}
                    zoomScale={zoomScale}
                    onZoomChange={setZoomScale}
                />
            )}

            <CustomModal
                isOpen={showRemoveModal}
                icon="🗑️"
                title="Remover Card"
                message="Tem certeza que deseja remover este card do subdeck?"
                type="danger"
                onConfirm={confirmRemoveCard}
                onCancel={() => {
                    setShowRemoveModal(false);
                    setCardToRemove(null);
                }}
            />

            {showModeModal && (
                <StudyModeModal
                    isOpen={showModeModal}
                    onClose={() => setShowModeModal(false)}
                    deckId={deckId!}
                    subdeckId={subdeckId!}
                    deckName={deckName}
                    subdeckName={subdeckName}
                    isCustomDeck={effectiveIsCustomDeck}
                />
            )}

            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}
        </div>
    );
};

export default ViewPage;